import { Box, Chip, Paper, Typography } from '@mui/material'
import { FC } from 'react'
import { useTranslate } from 'react-admin'

import { bombeoPrefill, SPECIAL_EVENT_TYPES } from '../../../data'
import { currentPlaceEvent, type EventRow, type ScaleRecord } from '../../../data/source'
import { ManualScaleBadge } from '../../atoms/ManualScaleBadge'
import { AddSpecialEventButton } from '../../molecules/AddSpecialEventButton'
import { ScaleInfoDialog } from '../../molecules/ScaleInfoDialog'
import { ScaleTimeline } from '../../molecules/ScaleTimeline'

export interface ScaleDetailsProps {
  scale: ScaleRecord
  /** The scale's events, in any order: the timeline orders them. */
  events: EventRow[]
}

/**
 * One scale: its vessel and state (as the list's Estado), buttons to add a
 * MARPOL or a BOMBEO event (any number of each; none on a closed scale),
 * then the Gantt chart of its events. Stays are read-only; MARPOL
 * and BOMBEO rows open their editor. Everything else is behind ℹ️ +Info.
 */
export const ScaleDetails: FC<ScaleDetailsProps> = ({ scale, events }) => {
  const translate = useTranslate()
  // A new BOMBEO starts from what SOSTRAT says the current stay's operations are.
  const now = Date.now()
  const currentStay = currentPlaceEvent(events, now)
  const prefill = bombeoPrefill(events.find(event => event.id === currentStay?.id)?.operations ?? [])

  return (
    <Box p={2} display="flex" flexDirection="column" gap={3}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" data-testid="scale-header">
        <Typography variant="h5" component="h2" fontWeight="bold" color="primary">
          {scale.scaleId}
        </Typography>
        {scale.vessel !== null && (
          <Typography variant="h6" component="span" color="text.secondary">
            {scale.vessel}
          </Typography>
        )}
        {scale.isManual && <ManualScaleBadge seenInSource={scale.seenInSource} />}
        {/* The list's Estado: one state, Finalizado winning over Cerrado. */}
        <Chip size="small" label={translate(`enums.ScaleState.${scale.state}`)} data-testid="scale-state" />
        <Box display="flex" gap={1} ml="auto">
          <ScaleInfoDialog scale={scale} events={events} />
          {!scale.closed &&
            SPECIAL_EVENT_TYPES.map(type => (
              <AddSpecialEventButton key={type} scale={scale} type={type} prefill={type === 'BOMBEO' ? prefill : undefined} />
            ))}
        </Box>
      </Box>

      {/* ETA/ETD and Inicio/Rendimiento/Salida are hidden until they come back in another form. */}

      {events.length === 0 ? (
        <Paper variant="outlined">
          <Typography p={2} color="text.secondary">
            {translate('pages.scales.no_events')}
          </Typography>
        </Paper>
      ) : (
        <ScaleTimeline events={events} now={now} />
      )}
    </Box>
  )
}
